/**
 * VDL Parsing Report Page
 * Daily VDL parsing reports with calendar navigation
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Calendar,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Spin,
  Badge,
  Select,
  Input,
  message,
  notification,
  Empty,
  Tooltip,
  Drawer,
  Segmented,
} from 'antd'
import {
  BarChartOutlined,
  PhoneOutlined,
  SendOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import vdlParsingReportService, {
  type VDLParsingReport,
  type VDLParsingReportItem,
  type VDLParsingReportListItem,
} from '@/services/vdlParsingReportService'

const { Title, Text } = Typography

const OPERATOR_NAMES: Record<string, string> = {
  M: 'МТС',
  BL: 'Билайн',
  RT: 'Ростелеком',
  BLBL: 'Билайн2',
  RTRT: 'Ростелеком2',
  MTMT: 'МТС2',
  B0: 'Б0',
  MGMG: 'Мегафон',
}

function VDLParsingReportPage() {
  const [loading, setLoading] = useState(true)
  const [reportLoading, setReportLoading] = useState(false)
  const [runLoading, setRunLoading] = useState(false)
  const [report, setReport] = useState<VDLParsingReport | null>(null)
  const [reportList, setReportList] = useState<VDLParsingReportListItem[]>([])
  const [dayReports, setDayReports] = useState<VDLParsingReport[]>([])
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [searchText, setSearchText] = useState('')
  const [managerFilter, setManagerFilter] = useState<string | undefined>(undefined)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [notifApi, notifCtx] = notification.useNotification()

  const loadReportList = useCallback(async () => {
    try {
      const list = await vdlParsingReportService.getList(90)
      setReportList(list)
    } catch (err) {
      console.error('Failed to load report list:', err)
    }
  }, [])

  const loadReportDetail = useCallback(async (reportId: number) => {
    setReportLoading(true)
    try {
      const detail = await vdlParsingReportService.getDetail(reportId)
      setReport(detail)
    } catch (err) {
      console.error('Failed to load report detail:', err)
      message.error('Ошибка загрузки отчёта')
    } finally {
      setReportLoading(false)
    }
  }, [])

  const loadReportByDate = useCallback(async (date: string) => {
    setReportLoading(true)
    try {
      const list = await vdlParsingReportService.getByDate(date)
      setDayReports(list)
      if (list.length === 0) {
        setReport(null)
        setReportLoading(false)
        return
      }
      // Newest first (backend sorts DESC). Pick latest, fetch full details with items.
      const latest = list[0]
      const detail = await vdlParsingReportService.getDetail(latest.id)
      setReport(detail)
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setDayReports([])
        setReport(null)
      } else {
        console.error('Failed to load report:', err)
        message.error('Ошибка загрузки отчёта')
      }
    } finally {
      setReportLoading(false)
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      try {
        await loadReportList()
        try {
          const latest = await vdlParsingReportService.getLatest()
          setReport(latest)
          if (latest?.report_date) {
            const latestDate = dayjs(latest.report_date)
            setSelectedDate(latestDate)
            // Also load all reports for that day so switching between them is available.
            try {
              const list = await vdlParsingReportService.getByDate(latestDate.format('YYYY-MM-DD'))
              setDayReports(list)
            } catch {
              setDayReports(latest ? [latest] : [])
            }
          }
        } catch {
          setReport(null)
          setDayReports([])
        }
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [loadReportList])

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date)
    loadReportByDate(date.format('YYYY-MM-DD'))
    setCalendarOpen(false)
  }

  const handleRunReport = async () => {
    setRunLoading(true)
    const notifKey = 'vdl-run-report'
    notifApi.open({
      key: notifKey,
      message: 'Генерация отчёта',
      description:
        'Идёт сбор данных из VDL и сравнение с последним отчётом. Обычно занимает 2-5 минут, не закрывайте страницу.',
      icon: <LoadingOutlined style={{ color: '#1890ff' }} spin />,
      duration: 0,
    })
    try {
      const result = await vdlParsingReportService.runReport()
      notifApi.destroy(notifKey)
      if (result.status === 'running') {
        message.warning(result.message || 'Другой отчёт уже выполняется, дождитесь его завершения')
      } else if (result.status === 'completed') {
        const today = dayjs()
        setSelectedDate(today)
        await loadReportList()
        const list = await vdlParsingReportService.getByDate(today.format('YYYY-MM-DD'))
        setDayReports(list)
        if (result.report_id) {
          const detail = await vdlParsingReportService.getDetail(result.report_id)
          setReport(detail)
        } else if (list.length > 0) {
          const detail = await vdlParsingReportService.getDetail(list[0].id)
          setReport(detail)
        }
        message.success('Отчёт сгенерирован')
      } else {
        message.warning(result.message)
      }
    } catch (err) {
      notifApi.destroy(notifKey)
      console.error('Failed to run report:', err)
      message.error('Ошибка запуска отчёта')
    } finally {
      setRunLoading(false)
    }
  }

  const dateCellRender = (date: Dayjs) => {
    const dateStr = date.format('YYYY-MM-DD')
    const dayItems = reportList.filter((r) => r.report_date === dateStr)
    if (dayItems.length === 0) return null
    // Pick worst/latest for the day for the issue badge
    const latest = dayItems[0]
    const color = latest.projects_with_issues > 0 ? '#ff4d4f' : '#52c41a'
    return (
      <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 4 }}>
        <Badge count={latest.projects_with_issues} size="small" style={{ backgroundColor: color }} overflowCount={999} />
        {dayItems.length > 1 && (
          <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
            ×{dayItems.length}
          </Tag>
        )}
      </div>
    )
  }

  const managerOptions = React.useMemo(() => {
    if (!report?.items) return []
    const names = new Set<string>()
    report.items.forEach((item) => {
      item.vdl_manager_names?.forEach((n: string) => names.add(n))
    })
    return Array.from(names).sort().map((n) => ({ label: n, value: n }))
  }, [report?.items])

  const filteredItems = React.useMemo(() => {
    if (!report?.items) return []
    let items = [...report.items]
    if (searchText) {
      const lower = searchText.toLowerCase()
      items = items.filter(
        (item) => item.project_name?.toLowerCase().includes(lower) || String(item.crm_id).includes(lower)
      )
    }
    if (managerFilter) {
      items = items.filter((item) => item.vdl_manager_names?.includes(managerFilter))
    }
    return items
  }, [report?.items, searchText, managerFilter])

  // Recalculate stats when manager filter is active
  const filteredStats = React.useMemo(() => {
    if (!report) return null
    if (!managerFilter) {
      return {
        total_active_projects: report.total_active_projects,
        projects_with_parse_flags: report.projects_with_parse_flags,
        projects_with_issues: report.projects_with_issues,
        phone_on_total: report.phone_on_total,
        phone_off_total: report.phone_off_total,
        ishod_on_total: report.ishod_on_total,
        ishod_off_total: report.ishod_off_total,
      }
    }
    const items = filteredItems
    return {
      total_active_projects: items.length,
      projects_with_parse_flags: items.length,
      projects_with_issues: items.filter((i) => i.has_source_issue || i.has_ops_off).length,
      phone_on_total: items.reduce((s, i) => s + i.phone_on, 0),
      phone_off_total: items.reduce((s, i) => s + i.phone_off, 0),
      ishod_on_total: items.reduce((s, i) => s + i.ishod_on, 0),
      ishod_off_total: items.reduce((s, i) => s + i.ishod_off, 0),
    }
  }, [report, managerFilter, filteredItems])

  // Filter deltas by manager's projects
  const filteredDeltas = React.useMemo(() => {
    if (!report?.deltas) return null
    if (!managerFilter) return report.deltas

    const managerCrmIds = new Set(filteredItems.map((i) => i.crm_id))

    const filterList = (list: any[]) =>
      list?.filter((p: any) => managerCrmIds.has(p.crm_id)) ?? []

    const newProblems = filterList(report.deltas.new_problems)
    const fixed = filterList(report.deltas.fixed)
    const worsened = filterList(report.deltas.worsened)
    const improved = filterList(report.deltas.improved)

    // Recalculate phone/ishod deltas from worsened/improved items
    let deltaPhoneOff = 0
    let deltaIshodOff = 0
    ;[...worsened, ...newProblems].forEach((p: any) => {
      deltaPhoneOff += (p.curr_phone_off ?? 0) - (p.prev_phone_off ?? 0)
      deltaIshodOff += (p.curr_ishod_off ?? 0) - (p.prev_ishod_off ?? 0)
    })
    ;[...improved, ...fixed].forEach((p: any) => {
      deltaPhoneOff += (p.curr_phone_off ?? 0) - (p.prev_phone_off ?? 0)
      deltaIshodOff += (p.curr_ishod_off ?? 0) - (p.prev_ishod_off ?? 0)
    })

    return {
      ...report.deltas,
      new_problems: newProblems,
      fixed,
      worsened,
      improved,
      delta_phone_off: deltaPhoneOff,
      delta_ishod_off: deltaIshodOff,
    }
  }, [report?.deltas, managerFilter, filteredItems])

  // Recalculate operator summary from filtered items
  const filteredOperatorSummary = React.useMemo(() => {
    if (!report) return null
    if (!managerFilter) return report.operator_summary

    const summary: Record<string, { on: number; off: number }> = {}
    filteredItems.forEach((item) => {
      if (!item.operators) return
      Object.entries(item.operators).forEach(([key, isOn]) => {
        if (!summary[key]) summary[key] = { on: 0, off: 0 }
        if (isOn) summary[key].on++
        else summary[key].off++
      })
    })
    return summary
  }, [report, managerFilter, filteredItems])

  const columns = [
    {
      title: 'CRM ID',
      dataIndex: 'crm_id',
      key: 'crm_id',
      width: 80,
      render: (id: number) => (
        <a href={`https://victory-crm.ru/crm/project/${id}/main`} target="_blank" rel="noopener noreferrer">
          {id}
        </a>
      ),
    },
    {
      title: 'Проект',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Менеджеры',
      dataIndex: 'vdl_manager_names',
      key: 'managers',
      width: 150,
      render: (names: string[] | null) =>
        names?.length ? (
          <Space size={[0, 2]} wrap>
            {names.map((n) => (
              <Tag key={n} color="blue" style={{ margin: 0, fontSize: 11 }}>
                {n}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 90,
      sorter: (a: VDLParsingReportItem, b: VDLParsingReportItem) => a.phone_off - b.phone_off,
      render: (_: unknown, item: VDLParsingReportItem) => (
        <Space size={4}>
          {item.phone_off > 0 ? (
            <Text type="danger" strong>{item.phone_off}</Text>
          ) : (
            <Text type="secondary">0</Text>
          )}
          <Text type="secondary">/</Text>
          <Text type="success">{item.phone_on}</Text>
        </Space>
      ),
    },
    {
      title: 'Ishod',
      key: 'ishod',
      width: 90,
      sorter: (a: VDLParsingReportItem, b: VDLParsingReportItem) => a.ishod_off - b.ishod_off,
      render: (_: unknown, item: VDLParsingReportItem) => (
        <Space size={4}>
          {item.ishod_off > 0 ? (
            <Text type="danger" strong>{item.ishod_off}</Text>
          ) : (
            <Text type="secondary">0</Text>
          )}
          <Text type="secondary">/</Text>
          <Text type="success">{item.ishod_on}</Text>
        </Space>
      ),
    },
    {
      title: 'Выкл. операторы',
      dataIndex: 'operators',
      key: 'operators',
      width: 180,
      render: (ops: Record<string, boolean> | null) => {
        if (!ops) return <Text type="secondary">-</Text>
        const offOps = Object.entries(ops).filter(([, on]) => !on)
        if (offOps.length === 0) return <Text style={{ color: '#52c41a', fontSize: 12 }}>все вкл</Text>
        return (
          <Space size={[2, 2]} wrap>
            {offOps.map(([key]) => (
              <Tag key={key} color="red" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
                {OPERATOR_NAMES[key] || key}
              </Tag>
            ))}
          </Space>
        )
      },
    },
    {
      title: 'Тип',
      key: 'issues',
      width: 100,
      filters: [
        { text: 'Источники', value: 'source' },
        { text: 'Операторы', value: 'ops' },
      ],
      onFilter: (value: React.Key | boolean, record: VDLParsingReportItem) => {
        if (value === 'source') return record.has_source_issue
        if (value === 'ops') return record.has_ops_off
        return true
      },
      render: (_: unknown, item: VDLParsingReportItem) => (
        <Space size={[2, 2]} wrap>
          {item.has_source_issue && (
            <Tag color="volcano" style={{ margin: 0, fontSize: 11 }}>SRC</Tag>
          )}
          {item.has_ops_off && (
            <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>OPS</Tag>
          )}
        </Space>
      ),
    },
  ]

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <Spin size="large" />
      </div>
    )
  }

  const deltas = filteredDeltas

  return (
    <div>
      {notifCtx}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Space size="middle" wrap>
          <Title level={3} style={{ margin: 0, whiteSpace: 'nowrap' }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            VDL Parsing Report
          </Title>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => setCalendarOpen(true)}
            style={{ fontWeight: 500 }}
          >
            {selectedDate.format('DD.MM.YYYY')}
          </Button>
          {managerOptions.length > 0 && (
            <Select
              placeholder="Все менеджеры"
              value={managerFilter}
              onChange={setManagerFilter}
              options={managerOptions}
              style={{ width: 200 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          )}
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadReportList()
              if (report) loadReportByDate(selectedDate.format('YYYY-MM-DD'))
            }}
          >
            Обновить
          </Button>
          <Button type="primary" icon={<PlayCircleOutlined />} loading={runLoading} onClick={handleRunReport}>
            Запустить отчёт
          </Button>
        </Space>
      </div>

      {/* Calendar drawer */}
      <Drawer
        title="Выбрать дату"
        placement="left"
        width={340}
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
      >
        <Calendar
          fullscreen={false}
          value={selectedDate}
          onSelect={handleDateSelect}
          cellRender={(date, info) => {
            if (info.type === 'date') return dateCellRender(date)
            return null
          }}
        />
        <div style={{ marginTop: 16, padding: '8px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Красный бейдж = количество проектов с проблемами
          </Text>
        </div>
      </Drawer>

      {/* Switcher between multiple reports of the selected day */}
      {dayReports.length > 1 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap align="center">
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined /> Отчёты за {selectedDate.format('DD.MM.YYYY')} ({dayReports.length}):
            </Text>
            <Segmented
              value={report?.id}
              onChange={(val) => {
                const id = Number(val)
                if (id && id !== report?.id) loadReportDetail(id)
              }}
              options={dayReports.map((r, idx) => {
                const ts = r.report_datetime || r.created_at
                const timeLabel = ts ? dayjs.utc(ts).local().format('HH:mm') : `#${r.id}`
                const runTag = r.run_type === 'manual' ? ' (ручной)' : r.run_type === 'daily' ? ' (авто)' : ''
                const latestMark = idx === 0 ? ' ★' : ''
                return {
                  label: `${timeLabel}${runTag}${latestMark}`,
                  value: r.id,
                }
              })}
            />
          </Space>
        </Card>
      )}

      {reportLoading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spin size="large" />
        </div>
      ) : report ? (
        <>
          {/* Stats cards — full width */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Активные проекты"
                  value={filteredStats?.total_active_projects ?? 0}
                  valueStyle={{ color: '#1890ff', fontSize: 28 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="С проблемами"
                  value={filteredStats?.projects_with_issues ?? 0}
                  suffix={`/ ${filteredStats?.projects_with_parse_flags ?? 0}`}
                  valueStyle={{ color: '#ff4d4f', fontSize: 28 }}
                  prefix={<WarningOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Phone выкл / вкл"
                  value={filteredStats?.phone_off_total ?? 0}
                  valueStyle={{ color: '#ff4d4f', fontSize: 28 }}
                  prefix={<PhoneOutlined />}
                  suffix={<span style={{ fontSize: 16, color: '#52c41a' }}>/ {filteredStats?.phone_on_total ?? 0}</span>}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic
                  title="Ishod выкл / вкл"
                  value={filteredStats?.ishod_off_total ?? 0}
                  valueStyle={{ color: '#ff4d4f', fontSize: 28 }}
                  prefix={<SendOutlined />}
                  suffix={<span style={{ fontSize: 16, color: '#52c41a' }}>/ {filteredStats?.ishod_on_total ?? 0}</span>}
                />
              </Card>
            </Col>
          </Row>

          {/* Deltas + Operators row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {/* Deltas */}
            {deltas && (
              <Col xs={24} md={14}>
                <Card
                  size="small"
                  title="Изменения за день"
                  style={{ height: '100%' }}
                  extra={
                    deltas.prev_date ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        vs{' '}
                        {deltas.prev_datetime
                          ? deltas.prev_date === report?.report_date
                            ? dayjs.utc(deltas.prev_datetime).local().format('HH:mm')
                            : dayjs.utc(deltas.prev_datetime).local().format('DD.MM HH:mm')
                          : dayjs(deltas.prev_date).format('DD.MM.YYYY')}
                      </Text>
                    ) : null
                  }
                >
                  {deltas.new_problems.length === 0 &&
                  deltas.fixed.length === 0 &&
                  deltas.worsened.length === 0 &&
                  deltas.improved.length === 0 ? (
                    <Text type="secondary">Без изменений</Text>
                  ) : (
                    <Row gutter={[12, 12]}>
                      {deltas.new_problems.length > 0 && (
                        <Col xs={24} sm={12}>
                          <div style={{ padding: '10px 14px', background: '#fff2f0', borderRadius: 8, border: '1px solid #ffccc7' }}>
                            <Text type="danger" strong style={{ fontSize: 13 }}>
                              <ArrowUpOutlined /> +{deltas.new_problems.length} новых проблем
                            </Text>
                            <div style={{ marginTop: 6 }}>
                              {deltas.new_problems.slice(0, 4).map((p) => (
                                <div key={p.crm_id} style={{ fontSize: 12, lineHeight: '20px' }}>
                                  <a href={`https://victory-crm.ru/crm/project/${p.crm_id}/main`} target="_blank" rel="noopener noreferrer">
                                    {p.name}
                                  </a>
                                </div>
                              ))}
                              {deltas.new_problems.length > 4 && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  ...и ещё {deltas.new_problems.length - 4}
                                </Text>
                              )}
                            </div>
                          </div>
                        </Col>
                      )}

                      {deltas.fixed.length > 0 && (
                        <Col xs={24} sm={12}>
                          <div style={{ padding: '10px 14px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                            <Text style={{ color: '#52c41a', fontSize: 13 }} strong>
                              <CheckCircleOutlined /> {deltas.fixed.length} исправлено
                            </Text>
                            <div style={{ marginTop: 6 }}>
                              {deltas.fixed.slice(0, 4).map((p) => (
                                <div key={p.crm_id} style={{ fontSize: 12, lineHeight: '20px' }}>{p.name}</div>
                              ))}
                              {deltas.fixed.length > 4 && (
                                <Text type="secondary" style={{ fontSize: 11 }}>...и ещё {deltas.fixed.length - 4}</Text>
                              )}
                            </div>
                          </div>
                        </Col>
                      )}

                      {deltas.worsened.length > 0 && (
                        <Col xs={24} sm={12}>
                          <div style={{ padding: '10px 14px', background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                            <Text style={{ color: '#fa8c16', fontSize: 13 }} strong>
                              <ArrowUpOutlined /> {deltas.worsened.length} ухудшились
                            </Text>
                            <div style={{ marginTop: 6 }}>
                              {deltas.worsened.slice(0, 3).map((p) => (
                                <div key={p.crm_id} style={{ fontSize: 12, lineHeight: '20px' }}>
                                  {p.name} <Text type="secondary">({p.prev_off} → {p.curr_off})</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Col>
                      )}

                      {deltas.improved.length > 0 && (
                        <Col xs={24} sm={12}>
                          <div style={{ padding: '10px 14px', background: '#e6fffb', borderRadius: 8, border: '1px solid #87e8de' }}>
                            <Text style={{ color: '#13c2c2', fontSize: 13 }} strong>
                              <ArrowDownOutlined /> {deltas.improved.length} улучшились
                            </Text>
                            <div style={{ marginTop: 6 }}>
                              {deltas.improved.slice(0, 3).map((p) => (
                                <div key={p.crm_id} style={{ fontSize: 12, lineHeight: '20px' }}>
                                  {p.name} <Text type="secondary">({p.prev_off} → {p.curr_off})</Text>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Col>
                      )}

                      {(deltas.delta_phone_off !== 0 || deltas.delta_ishod_off !== 0) && (
                        <Col span={24}>
                          <Space size="large" style={{ paddingTop: 4 }}>
                            <Tooltip title="Изменение phone_off">
                              <Text style={{ fontSize: 13 }}>
                                Phone off:{' '}
                                <Text strong style={{ color: deltas.delta_phone_off > 0 ? '#ff4d4f' : deltas.delta_phone_off < 0 ? '#52c41a' : undefined }}>
                                  {deltas.delta_phone_off > 0 ? '+' : ''}{deltas.delta_phone_off}
                                </Text>
                              </Text>
                            </Tooltip>
                            <Tooltip title="Изменение ishod_off">
                              <Text style={{ fontSize: 13 }}>
                                Ishod off:{' '}
                                <Text strong style={{ color: deltas.delta_ishod_off > 0 ? '#ff4d4f' : deltas.delta_ishod_off < 0 ? '#52c41a' : undefined }}>
                                  {deltas.delta_ishod_off > 0 ? '+' : ''}{deltas.delta_ishod_off}
                                </Text>
                              </Text>
                            </Tooltip>
                          </Space>
                        </Col>
                      )}
                    </Row>
                  )}
                </Card>
              </Col>
            )}

            {/* Operator summary */}
            {filteredOperatorSummary && Object.keys(filteredOperatorSummary).length > 0 && (
              <Col xs={24} md={deltas ? 10 : 24}>
                <Card size="small" title="Операторы" style={{ height: '100%' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Object.entries(filteredOperatorSummary).map(([key, val]) => {
                      const hasIssues = val.off > 0
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 12px',
                            background: hasIssues ? '#fff2f0' : '#f6ffed',
                            borderRadius: 6,
                            border: `1px solid ${hasIssues ? '#ffccc7' : '#b7eb8f'}`,
                          }}
                        >
                          <Text strong style={{ fontSize: 13 }}>{OPERATOR_NAMES[key] || key}</Text>
                          <Space size={12}>
                            {hasIssues && (
                              <Text type="danger" style={{ fontSize: 13 }}>
                                {val.off} выкл
                              </Text>
                            )}
                            <Text style={{ color: '#52c41a', fontSize: 13 }}>
                              {val.on} вкл
                            </Text>
                          </Space>
                        </div>
                      )
                    })}
                  </div>
                </Card>
              </Col>
            )}
          </Row>

          {/* Table — full width */}
          <Card
            size="small"
            title={`Проекты с проблемами (${filteredItems.length})`}
            extra={
              <Space>
                <Input
                  placeholder="Поиск..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
              </Space>
            }
          >
            <Table
              dataSource={filteredItems}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} проектов` }}
              scroll={{ x: 900 }}
            />
          </Card>
        </>
      ) : (
        <Card>
          <Empty description={`Нет отчёта за ${selectedDate.format('DD.MM.YYYY')}`} />
        </Card>
      )}
    </div>
  )
}

export default VDLParsingReportPage
